/**
 * Etichette e toni UI condivisi del CRM di piattaforma (pagine Aziende +
 * Questionari). Solo presentazione: i valori canonici vivono in
 * `types/platformCrm.ts`.
 */

import { Timestamp } from 'firebase/firestore';
import type { SoftChipTone } from '../SoftChip';
import type {
  CampoContattoId,
  CanaleAggiornamenti,
  StatoConsensoAggiornamenti,
  StatoRisposta,
} from '@shared/questionari';
import type {
  PlatformLeadFonte,
  PlatformLeadStato,
  TipoDomanda,
} from '../../types/platformCrm';

// ============================================================================
// STATO FUNNEL
// ============================================================================

/** Ordine canonico del funnel (usato per select e ordinamenti). */
export const STATO_ORDER: PlatformLeadStato[] = [
  'nuovo',
  'contattato',
  'questionario_inviato',
  'questionario_completato',
  'demo',
  'pilota',
  'convertito',
  'perso',
];

export const STATO_LABELS: Record<PlatformLeadStato, string> = {
  nuovo: 'Nuovo',
  contattato: 'Contattato',
  questionario_inviato: 'Questionario inviato',
  questionario_completato: 'Questionario completato',
  demo: 'Demo',
  pilota: 'Pilota',
  convertito: 'Convertito',
  perso: 'Perso',
};

export const STATO_TONES: Record<PlatformLeadStato, SoftChipTone> = {
  nuovo: 'default',
  contattato: 'info',
  questionario_inviato: 'warning',
  questionario_completato: 'primary',
  demo: 'info',
  pilota: 'warning',
  convertito: 'success',
  perso: 'error',
};

// ============================================================================
// FONTE
// ============================================================================

export const FONTE_ORDER: PlatformLeadFonte[] = [
  'manuale',
  'forniture_fotovoltaiche',
  'questionario',
  'campagna',
];

export const FONTE_LABELS: Record<PlatformLeadFonte, string> = {
  forniture_fotovoltaiche: 'Forniture Fotovoltaiche',
  questionario: 'Questionario',
  manuale: 'Manuale',
  campagna: 'Campagna',
};

// ============================================================================
// TIPI DOMANDA
// ============================================================================

export const TIPO_DOMANDA_LABELS: Record<TipoDomanda, string> = {
  testo_breve: 'Testo breve',
  testo_lungo: 'Testo lungo',
  scelta_singola: 'Scelta singola',
  scelta_multipla: 'Scelta multipla',
  scala_1_10: 'Scala 1-10',
  si_no: 'Sì / no',
  numero: 'Numero',
};

/** Tipi per cui ha senso il salto condizionale semplice (risposta chiusa a valore singolo). */
export const TIPI_CON_SALTO: TipoDomanda[] = ['scelta_singola', 'si_no'];

// ============================================================================
// ULTIMA SCHERMATA, CONSENSO, LINGUE
// ============================================================================

/** Nome di ogni campo dell'ultima schermata, e l'etichetta proposta quando lo si aggiunge. */
export const CAMPO_CONTATTO_LABELS: Record<CampoContattoId, string> = {
  email: 'Email',
  nome: 'Nome e cognome',
  ruolo: 'Ruolo in azienda',
  azienda: "Nome dell'azienda",
  telefono: 'Telefono',
  partita_iva: 'Partita IVA',
  citta: 'Città',
  provincia: 'Provincia',
};

export const CANALE_LABELS: Record<CanaleAggiornamenti, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
};

export const CONSENSO_LABELS: Record<StatoConsensoAggiornamenti | 'nessuno', string> = {
  nessuno: 'Non chiesto',
  da_confermare: 'Da confermare',
  confermato: 'Confermato',
  revocato: 'Revocato',
};

export const CONSENSO_TONES: Record<StatoConsensoAggiornamenti | 'nessuno', SoftChipTone> = {
  nessuno: 'default',
  da_confermare: 'warning',
  confermato: 'success',
  revocato: 'error',
};

export const STATO_RISPOSTA_LABELS: Record<StatoRisposta, string> = {
  attuale: 'Attuale',
  sostituita: 'Sostituita da un invio più recente',
  ritirata: 'Ritirata (la persona è uscita)',
};

/** Le lingue proposte per le traduzioni (il codice resta aperto: si può scriverne un altro). */
export const NOMI_LINGUA: Record<string, string> = {
  it: 'Italiano',
  en: 'Inglese',
  de: 'Tedesco',
  fr: 'Francese',
  es: 'Spagnolo',
};

export function nomeLingua(codice: string | null | undefined): string {
  if (!codice) return '—';
  return NOMI_LINGUA[codice] ?? codice.toUpperCase();
}

// ============================================================================
// FORMATTAZIONE
// ============================================================================

/** Millisecondi da un Timestamp Firestore, una Date o un numero (null se assente o pendente). */
export function millisDi(value: unknown): number | null {
  if (value instanceof Timestamp) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Data + ora leggibile it-IT ('—' se assente). */
export function formatDateTime(value: unknown): string {
  const ms = millisDi(value);
  if (ms === null) return '—';
  return new Date(ms).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Sola data leggibile it-IT ('—' se assente). */
export function formatDate(value: unknown): string {
  const ms = millisDi(value);
  if (ms === null) return '—';
  return new Date(ms).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/** Durata compilazione leggibile (es. "3 min 12 s"). */
export function formatDurata(seconds?: number | null): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '—';
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return min > 0 ? `${min} min ${sec} s` : `${sec} s`;
}

/** Valore di risposta in forma leggibile (array → elenco, boolean → sì/no). */
export function formatRispostaValore(valore: unknown): string {
  if (valore === null || valore === undefined || valore === '') return '—';
  if (Array.isArray(valore)) return valore.map(String).join(', ');
  if (typeof valore === 'boolean') return valore ? 'sì' : 'no';
  return String(valore);
}
